import styled from "styled-components";

import { colorsPalettes, rgba } from "@similarweb/styles";
import { ChartTooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { AboveAverage, BelowAverage } from "pro-features/components/Pill/AboveBelowAverage";
import { translate } from "app/filters/i18nFilter";
import type { IDurationObject } from "app/services/DurationService";
import { formatDate } from "@similarweb/pro-logic-components";

import { type ChartTooltipColumnDefinition, ChartTooltipWrapper } from "../../../components/ChartTooltipWrapper";

import type { getAverageValues } from "./chartDataUtils";
import { axisConfig } from "./chartDataUtils";
import type { ChartDataPointT, ChartDataT } from "./types";

const TooltipContainer = styled.div`
    padding: 8px 12px 12px 12px;
    background-color: ${colorsPalettes.carbon[0]};
    box-shadow: 0 3px 6px 0 ${rgba(colorsPalettes.carbon[200], 0.5)};
    border-radius: 5px;
`;

type ChartTooltipData = {
    metric: string;
    previousPeriodValue?: string;
    value: string;
    aboveAverage: boolean;
};

const getColumnsDefinition = (isPeriodOverPeriod: boolean, currentPeriod: string, previousPeriod: string) => {
    const columnDefinition: ChartTooltipColumnDefinition<ChartTooltipData>[] = [
        {
            key: "metric",
            title: translate("Metric"),
            selector: "metric",
            align: "left",
            size: "100px",
            borderRight: true,
            render: ({ metric }) => {
                return <ChartTooltip.LabelCell label={metric} />;
            },
        },
        {
            key: "previousPeriodValue",
            title: previousPeriod,
            selector: "previousPeriodValue",
            align: "right",
            borderRight: true,
            size: isPeriodOverPeriod ? "100px" : ChartTooltip.ValueCellDefaultwidth,
            render: ({ previousPeriodValue }) => {
                return <ChartTooltip.ValueCell value={previousPeriodValue} />;
            },
        },
        {
            key: "value",
            title: isPeriodOverPeriod ? currentPeriod : translate("Value"),
            selector: "value",
            align: "right",
            borderRight: true,
            size: isPeriodOverPeriod ? "100px" : ChartTooltip.ValueCellDefaultwidth,
            render: ({ value }) => {
                return <ChartTooltip.ValueCell value={value} />;
            },
        },
        {
            key: "aboveAverage",
            title: null,
            selector: "aboveAverage",
            align: "right",
            size: ChartTooltip.ChangeCellDefaultWidth,
            render: ({ aboveAverage }) => {
                return aboveAverage === null ? (
                    <Flex justifyContent="flex-end">-</Flex>
                ) : (
                    <Flex justifyContent="flex-end">{aboveAverage ? <AboveAverage /> : <BelowAverage />}</Flex>
                );
            },
        },
    ];

    if (!isPeriodOverPeriod) {
        return columnDefinition.filter((column) => column.selector !== "previousPeriodValue");
    }

    return columnDefinition;
};

type Volume = {
    value: number;
    change: number;
};

const getTooltipData = (
    { totalVolumeAverage, volumeChangeAverage }: ReturnType<typeof getAverageValues>,
    currentPeriodVolume: Volume,
    previousPeriodVolume: Volume | undefined,
): Array<ChartTooltipData> => {
    return [
        {
            metric: translate("demand.analysis.search.trends.keywords.breakdown.scatterPlot.tooltip.xAxis.metric"),
            previousPeriodValue: axisConfig.xAxis.formatter(previousPeriodVolume?.value),
            value: axisConfig.xAxis.formatter(currentPeriodVolume.value),
            aboveAverage: currentPeriodVolume.value > totalVolumeAverage,
        },
        {
            metric: translate("demand.analysis.search.trends.keywords.breakdown.scatterPlot.tooltip.yAxis.metric"),
            previousPeriodValue: axisConfig.yAxis.formatter(previousPeriodVolume?.change),
            value: axisConfig.yAxis.formatter(currentPeriodVolume.change),
            aboveAverage: currentPeriodVolume.change > volumeChangeAverage,
        },
    ];
};

interface KeywordGroupsScatterPlotDataLabelTooltipProps {
    isPeriodOverPeriod: boolean;
    title: string;
    data: ChartDataPointT;
    chartData: ChartDataT;
    averageValues: ReturnType<typeof getAverageValues>;
    subtitleFiltersDateValue: IDurationObject<string>;
}

export const KeywordGroupsScatterPlotDataLabelTooltip = ({
    isPeriodOverPeriod,
    title,
    data,
    chartData,
    averageValues,
    subtitleFiltersDateValue,
}: KeywordGroupsScatterPlotDataLabelTooltipProps) => {
    const currentPeriod = formatDate(subtitleFiltersDateValue.from, subtitleFiltersDateValue.to);
    const previousPeriod = formatDate(subtitleFiltersDateValue.compareFrom, subtitleFiltersDateValue.compareTo);
    const selectedPointVolume = {
        value: data.x,
        change: data.y,
    };
    const connectedPoint = chartData.find((item) => item.name === data.name)?.data.find((item) => item.id !== data.id);
    const connectedPointVolume = connectedPoint ? { value: connectedPoint.x, change: connectedPoint.y } : undefined;

    let chartTooltipData: ChartTooltipData[];

    if (isPeriodOverPeriod && connectedPointVolume !== undefined) {
        if (connectedPoint.isPreviousPeriod) {
            chartTooltipData = getTooltipData(averageValues, selectedPointVolume, connectedPointVolume);
        } else {
            chartTooltipData = getTooltipData(averageValues, connectedPointVolume, selectedPointVolume);
        }
    } else {
        chartTooltipData = getTooltipData(averageValues, selectedPointVolume, undefined);
    }

    return (
        <TooltipContainer>
            <ChartTooltipWrapper<ChartTooltipData>
                title={title}
                subtitle={isPeriodOverPeriod ? undefined : currentPeriod}
                data={chartTooltipData}
                columnDefinition={getColumnsDefinition(isPeriodOverPeriod, currentPeriod, previousPeriod)}
            />
        </TooltipContainer>
    );
};
